import { Button } from 'app/shared/button';
import { toReadableNumber } from 'app/shared/utils/number';
import { VirtualAccountContext } from 'contexts/checkout/virtual';
import { useTenantService } from 'hooks/iam/tenant';
import { useCurrencyService } from 'hooks/sales/currency';
import { usePaymentService } from 'hooks/sales/payment';
import { useWalletService } from 'hooks/sales/wallet';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styles from './virtual.module.css';
import { Spacer } from 'app/layouts/generic';
import { InfoBanner } from 'app/shared/info-banner';
import Spinner from 'app/shared/spinners/spinner-25/spinner-25';
import { GridColumn, GridRow } from 'app/layouts/grid';

const accountDetails = ({
  account_name,
  account_number,
  bank_name,
  amount,
  currency_symbol,
  plan_name
}) => (
  <div>
    <div>
      To complete your purchase of the <b>{plan_name}</b> Plan, make a transfer to the following
      temporary account:
    </div>
    <br />
    <div>
      <b>Account Name: </b>
      {account_name}
    </div>
    <div>
      <b>Account Number: </b>
      {account_number}
    </div>
    <div>
      <b>Bank Name: </b>
      {bank_name}
    </div>
    <div>
      <b>Amount to Transfer: </b>
      {currency_symbol}
      {toReadableNumber(amount)}
    </div>
  </div>
);

const warning = (
  <div>
    <div>
      ⚠️ <b>PLEASE NOTE:</b>
    </div>
    <br />
    <div>- The account expires in 24 hours</div>
    <div>- Transfer the exact amount shown above</div>
    <div>
      - This is a one-time account for this transaction only.{' '}
      <b>DO NOT SAVE THIS ACCOUNT AS A BENEFICIARY</b>
    </div>
  </div>
);

const verified_text = `Payment Confirmed!

Your subscription is being activated. You'll be redirected to your dashboard in a moment.
`;

const foot_text = `Need help? Send an email to support@go-mailer.com`;

const displayPreText = (text) => (
  <div>
    {' '}
    <p style={{ whiteSpace: 'pre-wrap' }}>{text}</p>
  </div>
);

export const VirtualAccountCheckout = () => {
  const user_data = useSelector((state) => state.user_data);
  const { payment_details } = useContext(VirtualAccountContext);
  const { getCurrencySymbol } = useCurrencyService();
  const { fetchPayments } = usePaymentService();
  const { fetchTenant } = useTenantService();
  const { createVirtualAccount } = useWalletService();

  const [account_details, setAccountDetails] = useState({});
  const [is_generating, setIsGenerating] = useState(false);
  const [is_verified, setIsVerified] = useState(false);
  const [is_verifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (!payment_details || !payment_details.payment_id) return;
    if (!account_details || !account_details.account_number) return;

    const interval = setInterval(() => {
      handlePaymentVerification(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [account_details.account_number]);

  useEffect(() => {
    if (!payment_details || !payment_details.payment_id) return;
    handleAccountGeneration();
  }, [payment_details?.amount]);

  const handleAccountGeneration = async () => {
    setIsGenerating(true);
    const { tenant } = await fetchTenant(user_data.tenant_id);
    if (!tenant || !payment_details || !payment_details.payment_id) {
      setIsGenerating(false);
      return toast.error('Unable to retrieve account details. Please try again later.');
    }
    const data = {
      amount: payment_details?.amount,
      tenant_id: tenant.id,
      tx_ref: payment_details?.tx_ref,
      user: { ...user_data, name: tenant.name, email: tenant.emails[0] }
    };

    const result = await createVirtualAccount(data);
    if (result) {
      setAccountDetails(result);
    }
    setIsGenerating(false);
  };

  const handleFlutterwavePayment = async () => {
    window.FlutterwaveCheckout(payment_details?.flw_config);
  };

  const handlePaymentVerification = async (show_error = true) => {
    setIsVerifying(true);
    const {
      payments: [payment]
    } = await fetchPayments({ query_string: `id=${payment_details?.payment_id}&bool=is_verified` });

    if (payment && payment.is_verified) {
      setIsVerified(true);
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 5000);
    } else {
      if (show_error) {
        toast.info('Payment Not Yet Received');
      }
    }
    setIsVerifying(false);
  };

  return is_generating ? (
    <div className={styles.spinnerWrapper}>
      <Spinner />
    </div>
  ) : (
    <div>
      <div>
        {!is_verified ? (
          <>
            <div className={styles.infoText}>
              {accountDetails({
                ...account_details,
                plan_name: payment_details?.plan_name,
                amount: payment_details?.amount,
                currency_symbol: getCurrencySymbol(payment_details?.currency)
              })}
            </div>
            <br />
            <InfoBanner message={warning} type="error" />
          </>
        ) : (
          <div className={styles.successText}>{displayPreText(verified_text)}</div>
        )}{' '}
      </div>
      <Spacer multiple={4} />
      <GridRow num_of_columns={3}>
        <GridColumn>
          {payment_details?.amount < 400000 && (
            <Button text="Pay with card" onClick={handleFlutterwavePayment} type="secondary" />
          )}
        </GridColumn>
        <GridColumn span={2}>
          <Button
            text="I have made payment"
            onClick={handlePaymentVerification}
            loading={is_verifying}
            disabled={is_verifying}
            stop_count={is_verified}
            delay_interval={30000}
          />
        </GridColumn>
      </GridRow>
      <Spacer multiple={4} />
      {displayPreText(foot_text)}
    </div>
  );
};
